<script>
import { mapGetters } from 'vuex';
import {
  applyDialingPrefix,
  isValidPhoneNumber,
} from 'dashboard/helper/phoneHelper';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';

export default {
  name: 'PhoneDialForm',
  components: {
    WootDropdownItem,
    WootDropdownMenu,
  },
  props: {
    initialPhoneNumber: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  data() {
    return {
      phoneNumber: this.initialPhoneNumber,
      validationError: '',
      showInboxMenu: false,
    };
  },
  computed: {
    ...mapGetters({ phoneInboxes: 'inboxes/getPhoneInboxes' }),
    // Phone inboxes the agent can call from: the PBX picks the outgoing number
    // by the inbox's dialing prefix, so one without a prefix has nothing to offer.
    dialFromInboxes() {
      return this.phoneInboxes.filter(inbox => inbox.phone_dialing_prefix);
    },
    canDial() {
      return this.phoneNumber.trim().length >= 3;
    },
  },
  watch: {
    initialPhoneNumber(newValue) {
      this.phoneNumber = newValue;
      if (newValue) {
        this.$nextTick(() => {
          this.focusInputAtStart();
        });
      }
    },
  },
  mounted() {
    if (this.initialPhoneNumber) {
      this.$nextTick(() => {
        this.focusInputAtStart();
      });
    }
  },
  methods: {
    validatePhoneNumber(number) {
      if (!this.phoneNumber.trim()) {
        this.validationError = this.$t('PHONE_DIAL.VALIDATION.REQUIRED');
        return false;
      }
      if (!isValidPhoneNumber(number)) {
        this.validationError = this.$t('PHONE_DIAL.VALIDATION.INVALID_FORMAT');
        return false;
      }
      this.validationError = '';
      return true;
    },
    async dialNumber(number = this.phoneNumber) {
      if (!this.canDial || !this.validatePhoneNumber(number)) {
        return;
      }

      await this.$store.dispatch('phone/makeCall', {
        number: number.trim(),
      });

      this.phoneNumber = '';
      this.validationError = '';

      this.$emit('close');
    },
    dialFromInbox(inbox) {
      this.showInboxMenu = false;
      this.dialNumber(
        applyDialingPrefix(this.phoneNumber, inbox.phone_dialing_prefix)
      );
    },
    toggleInboxMenu() {
      this.showInboxMenu = !this.showInboxMenu;
    },
    closeInboxMenu() {
      this.showInboxMenu = false;
    },
    onInput() {
      if (this.validationError) {
        this.validationError = '';
      }
    },
    focusInputAtStart() {
      const input = this.$refs.phoneInput;
      if (input) {
        input.focus();
        input.setSelectionRange(0, 0);
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="flex gap-2 items-center">
      <input
        ref="phoneInput"
        v-model="phoneNumber"
        type="tel"
        class="flex-1 px-3 text-sm border rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:bg-n-alpha-2 disabled:text-n-slate-9 disabled:cursor-not-allowed dark:bg-n-slate-1 dark:border-n-slate-6 dark:text-n-slate-12 dark:placeholder-n-slate-9 dark:focus:border-blue-400"
        :class="{
          'border-n-strong bg-white text-n-slate-12': !validationError,
          'border-red-500 bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-300 dark:border-red-400':
            validationError,
        }"
        :placeholder="$t('PHONE_DIAL.PLACEHOLDER')"
        @input="onInput"
      />
      <div
        v-on-clickaway="closeInboxMenu"
        class="relative flex items-center mb-4"
      >
        <button
          v-tooltip.top="$t('PHONE_DIAL.ACTIONS.DIAL')"
          class="w-18 h-18 rounded-full border-2 transition-all duration-200"
          :class="{
            'border-green-500 text-green-500 hover:bg-green-500 hover:text-white hover:scale-105 dark:border-green-400 dark:text-green-400 dark:hover:bg-green-400 dark:hover:text-n-slate-1':
              canDial && !validationError,
            'border-n-slate-6 text-n-slate-6 cursor-not-allowed dark:border-n-slate-7 dark:text-n-slate-7':
              !canDial || validationError,
          }"
          @click="dialNumber()"
        >
          <fluent-icon icon="phone" size="24" icon-lib="tabler" />
        </button>
        <button
          v-if="dialFromInboxes.length"
          v-tooltip.top="$t('PHONE_DIAL.ACTIONS.DIAL_FROM')"
          data-testid="dial-from-toggle"
          class="flex items-center h-6 px-0.5 rounded transition-colors"
          :class="{
            'text-green-500 hover:bg-green-500/10 dark:text-green-400':
              canDial && !validationError,
            'text-n-slate-6 cursor-not-allowed dark:text-n-slate-7':
              !canDial || validationError,
          }"
          :disabled="!canDial"
          @click="toggleInboxMenu"
        >
          <span class="i-lucide-chevron-down size-4" />
        </button>
        <div
          v-if="showInboxMenu"
          class="dropdown-pane dropdown-pane--open bottom-full mb-1 left-auto ltr:right-0 rtl:left-0 min-w-[13rem] max-w-[18rem]"
        >
          <div class="px-2 pb-1 text-xs font-medium text-n-slate-11">
            {{ $t('PHONE_DIAL.DIAL_FROM.TITLE') }}
          </div>
          <WootDropdownMenu class="mb-0">
            <WootDropdownItem
              v-for="inbox in dialFromInboxes"
              :key="inbox.id"
              class="mb-0"
            >
              <button
                data-testid="dial-from-inbox"
                class="flex items-center justify-between w-full gap-2 px-2 py-1.5 text-sm rounded-md text-n-slate-12 ltr:text-left rtl:text-right hover:bg-n-alpha-2"
                @click="dialFromInbox(inbox)"
              >
                <span class="min-w-0">
                  <span class="block truncate">{{ inbox.name }}</span>
                  <span class="block text-xs truncate text-n-slate-11">
                    {{ (inbox.phone_numbers || []).join(', ') }}
                  </span>
                </span>
                <span
                  class="px-1 text-xs rounded shrink-0 bg-n-alpha-2 text-n-slate-11"
                >
                  {{ inbox.phone_dialing_prefix }}
                </span>
              </button>
            </WootDropdownItem>
          </WootDropdownMenu>
        </div>
      </div>
    </div>
    <div
      v-if="validationError"
      class="text-red-600 text-xs mt-1 ml-1 dark:text-red-400"
    >
      {{ validationError }}
    </div>
  </div>
</template>
